/**
 * PutAwayPage
 * Kitchly v0.5.0
 *
 * Batch placement screen — assign locations to standby items, then place into inventory.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  PackageOpen,
  MapPin,
  CheckCircle,
  Package
} from 'lucide-react';
import { shopping, locations as locationsApi } from '../services/api';

function PutAwayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [placements, setPlacements] = useState({}); // { itemId: locationId }
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [standbyData, locData] = await Promise.all([
        shopping.standby(),
        locationsApi.list()
      ]);

      setItems(standbyData.items || []);
      setLocations(locData.locations || []);

      // Pre-fill placements from smart suggestions
      const initialPlacements = {};
      for (const item of (standbyData.items || [])) {
        if (item.target_location_id) {
          initialPlacements[item.id] = item.target_location_id;
        } else if (item.suggested_location) {
          initialPlacements[item.id] = item.suggested_location.id;
        }
      }
      setPlacements(initialPlacements);
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLocationChange = (itemId, locationId) => {
    setPlacements(prev => ({
      ...prev,
      [itemId]: locationId ? parseInt(locationId) : null
    }));
  };

  const handlePlaceAll = async () => {
    // Check all items have locations
    const unassigned = items.filter(item => !placements[item.id]);
    if (unassigned.length > 0) {
      toast.error(t('putAway.assignAll'));
      return;
    }

    setPlacing(true);
    try {
      const placementData = items.map(item => ({
        item_id: item.id,
        location_id: placements[item.id]
      }));

      const result = await shopping.place(placementData);
      toast.success(t('putAway.done', { count: result.placed?.length || items.length }));
      navigate('/inventory');
    } catch {
      toast.error(t('common.error'));
    } finally {
      setPlacing(false);
    }
  };

  if (loading) {
    return <div className="page-loader"><div className="loader-spinner" /></div>;
  }

  return (
    <div className="put-away-page" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-md)', gap: 'var(--space-md)' }}>
        <button className="btn-icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0 }}>{t('putAway.title')}</h1>
          <p className="text-sm text-secondary" style={{ margin: 0 }}>{t('putAway.subtitle')}</p>
        </div>
      </div>

      <div style={{ padding: '0 var(--space-md) var(--space-xl)' }}>
        {items.length === 0 ? (
          <div className="empty-state">
            <CheckCircle size={64} className="empty-state-icon" color="var(--color-primary)" />
            <h3>{t('putAway.empty')}</h3>
            <button className="btn btn-secondary mt-md" onClick={() => navigate('/shopping')}>
              {t('putAway.back')}
            </button>
          </div>
        ) : (
          <>
            {/* Items with location selectors */}
            {items.map(item => {
              const itemName = item.custom_name || item.product_name || 'Unknown Item';
              const hasSuggestion = item.suggested_location;

              return (
                <div key={item.id} className="card mb-md">
                  <div className="flex items-center gap-md mb-md">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={itemName}
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                      />
                    ) : (
                      <div
                        className="list-item-icon"
                        style={{ width: 48, height: 48, background: 'var(--color-primary-bg)' }}
                      >
                        <Package size={22} color="var(--color-primary)" />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '15px' }}>{itemName}</div>
                      <div className="text-sm text-secondary">
                        {item.quantity} {item.unit}
                        {item.brand && ` — ${item.brand}`}
                      </div>
                    </div>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">
                      <MapPin size={14} style={{ verticalAlign: 'middle' }} /> {t('putAway.selectLocation')}
                    </label>
                    <select
                      className="form-select"
                      value={placements[item.id] || ''}
                      onChange={(e) => handleLocationChange(item.id, e.target.value)}
                    >
                      <option value="">{t('putAway.selectLocation')}</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                    {hasSuggestion && !placements[item.id] && (
                      <button
                        className="text-xs mt-sm"
                        onClick={() => handleLocationChange(item.id, hasSuggestion.id)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-primary)', fontWeight: 500,
                          padding: 0, textDecoration: 'underline'
                        }}
                      >
                        {t('putAway.suggested', { location: hasSuggestion.name })}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Place All button */}
            <div style={{ marginTop: 'var(--space-lg)' }}>
              <button
                className="btn btn-primary btn-full btn-lg"
                onClick={handlePlaceAll}
                disabled={placing}
              >
                <PackageOpen size={20} />
                {placing ? t('putAway.placing') : t('putAway.placeAll')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PutAwayPage;
